/**
 * Turns the engine's coverage figures into the assessment a screen shows, or the reason it cannot.
 *
 * The checks run in order: a failed load or save → `failed`; loading or saving → `loading`; no usable
 * cost estimate or care timing → `unavailable`; no complete self-funding account when the host requires
 * one → `unavailable`; an engine figure missing despite complete inputs → `unavailable`. Only then is
 * the result `ready`. An `undefined` engine figure is never read as `0`.
 *
 * Flow policy is explicit: the D2C self-funding projection requires an account, guided planning does
 * not (a guided user may only have an existing policy); guided counts recorded policies only when that
 * type was selected, D2C always counts them.
 */
import type { FundingCalculation } from './calculation-state';

export interface FundingAssessmentInputs {
  clientStatus: 'loading' | 'ready' | 'failed';
  retryClient: () => void;
  saveStatus: 'idle' | 'saving' | 'failed';
  retrySave: () => void;
  totalLtcCost: number | null | undefined;
  yearsTillLtc: number | null | undefined;
  completeAccountCount: number;
  selfFundingCoverage: number | null | undefined;
  policyCoverage: number | null | undefined;
  /** Engine combined coverage, or a completed guided session's self + policy snapshot sum. */
  combinedCoverage: number | null | undefined;
  requireSelfFundingAccount: boolean;
  includeRecordedPolicies: boolean;
}

export interface FundingAssessment {
  totalLtcCost: number;
  yearsTillLtc: number;
  /** null when there are no complete self-funding accounts. */
  selfFundingCoverage: number | null;
  /** null when recorded policies are not counted. */
  policyCoverage: number | null;
  /** Coverage counted against the cost: combined when policies count, self-funding otherwise. */
  combinedCoverage: number;
  gap: number;
}

function isUsableNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function deriveFundingAssessment(
  inputs: FundingAssessmentInputs,
): FundingCalculation<FundingAssessment> {
  if (inputs.clientStatus === 'failed') return { status: 'failed', retry: inputs.retryClient };
  if (inputs.saveStatus === 'failed') return { status: 'failed', retry: inputs.retrySave };
  if (inputs.clientStatus === 'loading' || inputs.saveStatus === 'saving')
    return { status: 'loading' };

  if (!isUsableNumber(inputs.totalLtcCost) || inputs.totalLtcCost <= 0) {
    return { status: 'unavailable', reason: 'missing-cost-estimate' };
  }
  if (!isUsableNumber(inputs.yearsTillLtc)) {
    return { status: 'unavailable', reason: 'missing-care-timing' };
  }

  const hasAccounts = inputs.completeAccountCount > 0;
  if (inputs.requireSelfFundingAccount && !hasAccounts) {
    return { status: 'unavailable', reason: 'no-complete-account' };
  }

  let selfFundingCoverage: number | null = null;
  if (hasAccounts) {
    if (!isUsableNumber(inputs.selfFundingCoverage)) {
      return { status: 'unavailable', reason: 'calculation-missing' };
    }
    selfFundingCoverage = inputs.selfFundingCoverage;
  }

  let policyCoverage: number | null = null;
  let combinedCoverage: number;
  if (inputs.includeRecordedPolicies) {
    if (!isUsableNumber(inputs.combinedCoverage)) {
      return { status: 'unavailable', reason: 'calculation-missing' };
    }
    policyCoverage = isUsableNumber(inputs.policyCoverage) ? inputs.policyCoverage : 0;
    combinedCoverage = inputs.combinedCoverage;
  } else {
    // Without accounts and without policies there is genuinely nothing covering the cost.
    combinedCoverage = selfFundingCoverage ?? 0;
  }

  return {
    status: 'ready',
    value: {
      totalLtcCost: inputs.totalLtcCost,
      yearsTillLtc: Math.max(0, Math.round(inputs.yearsTillLtc)),
      selfFundingCoverage,
      policyCoverage,
      combinedCoverage,
      gap: Math.max(0, inputs.totalLtcCost - combinedCoverage),
    },
  };
}
